

import React, { useState, useEffect } from "react";
import { Card, Button, Row, Col, Alert, ProgressBar } from "react-bootstrap";
import { FaUpload, FaCamera, FaCheck, FaTimes } from "react-icons/fa";
import CameraCapture from "./CameraCapture";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const DocumentUpload = ({ empleado }) => {
  const legajo = empleado?.legajo;
  const cuil = empleado?.cuil;

  const [images, setImages] = useState({
    selfie: null,
    dniFront: null,
    dniBack: null,
    carnetFront: null,
    carnetBack: null
  });

  const [cameraType, setCameraType] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [message, setMessage] = useState("");
  const [fotosExistentes, setFotosExistentes] = useState({});

  useEffect(() => {
    const tipoMap = {
      "Selfie": "selfie",
      "DNI/Frente": "dniFront",
      "DNI/Dorso": "dniBack",
      "Carnet/Frente": "carnetFront",
      "Carnet/Dorso": "carnetBack"
    };

    if (!legajo) return;

    // Normalizar API_URL (remover barra final si existe)
    const baseURL = API_URL.replace(/\/$/, '');
    
    fetch(`${baseURL}/api/fotos/documents`)
      .then(res => res.json())
      .then(data => {
        const fotos = data[legajo] || [];
        const estado = {};

        fotos.forEach(f => {
          const tipo = tipoMap[f.tipo];
          if (tipo) {
            estado[tipo] = f.url;
          }
        })

        setFotosExistentes(estado);
      })
      .catch(err => {
        console.error("❌ Error al cargar fotos existentes:", err);
      });
  }, [legajo]);


  const openCamera = (type) => {
    setCameraType(type);
    setShowCamera(true);
  };

  const handleCapture = (image) => {
    if (image && cameraType) {
      setImages(prev => ({ ...prev, [cameraType]: image }));
      setUploadStatus(prev => ({ ...prev, [cameraType]: null }));
    }
  };

  const removeImage = (type) => {
    setImages(prev => ({ ...prev, [type]: null }));
    setUploadStatus(prev => ({ ...prev, [type]: null }));
  };

  const uploadAllImages = async () => {
    setLoading(true);
    setMessage("🔄 Iniciando carga de imágenes...");

    const imageTypes = Object.keys(images);
    const imagesToUpload = imageTypes.filter(type => images[type]);

    if (imagesToUpload.length === 0) {
      setMessage("⚠️ No hay imágenes para cargar");
      setLoading(false);
      return;
    }

    try {
      // Comprimir y preparar todas las imágenes primero
      const uploadPromises = imagesToUpload.map(async (type) => {
        setUploadStatus(prev => ({ ...prev, [type]: "uploading" }));

        const response = await fetch(images[type]);
        const blob = await response.blob();
        
        // Comprimir imagen antes de subir
        const compressedBlob = await compressImage(blob, 0.8); // 80% calidad
        const file = new File([compressedBlob], `${type}.jpg`, { type: "image/jpeg" });

        const formData = new FormData();
        formData.append("image", file);
        formData.append("tipo", type);
        formData.append("legajo", legajo);
        formData.append("nombreArchivo", type === "selfie" ? `${cuil}_${type}` : `${legajo}_${type}`);

        try {
          const uploadResponse = await axios.post(
            `${API_URL}/api/fotos/upload-single`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 30000
            }
          );

          if (uploadResponse.data.success) {
            setUploadStatus(prev => ({ ...prev, [type]: "success" }));
            return { type, success: true };
          } else {
            setUploadStatus(prev => ({ ...prev, [type]: "error" }));
            return { type, success: false };
          }
        } catch (error) {
          console.error(`Error subiendo ${type}:`, error);
          setUploadStatus(prev => ({ ...prev, [type]: "error" }));
          return { type, success: false };
        }
      });

      // Ejecutar todas las cargas en paralelo
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(r => r.success).length;

      setLoading(false);

      if (successfulUploads === imagesToUpload.length) {
        setMessage("🎉 ¡Todas las imágenes se subieron correctamente!");
        alert("✅ Carga completada con éxito");
      } else {
        setMessage(`⚠️ Se cargaron ${successfulUploads} de ${imagesToUpload.length} imágenes`);
        alert(`Se completaron ${successfulUploads} de ${imagesToUpload.length} cargadas`);
      }
    } catch (error) {
      console.error("Error general:", error);
      setMessage("❌ Error durante la carga");
      setLoading(false);
    }
  };

  // Función para comprimir imágenes
  const compressImage = (blob, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Redimensionar si es muy grande (máximo 1920px de ancho)
        let width = img.width;
        let height = img.height;
        const maxWidth = 1920;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((compressedBlob) => {
          resolve(compressedBlob || blob);
        }, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(blob);
    });
  };

  const renderStatusIcon = (type) => {
    const status = uploadStatus[type];
    if (!status) return null;

    switch (status) {
      case "success":
        return <FaCheck className="text-success ms-2" />;
      case "error":
        return <FaTimes className="text-danger ms-2" />;
      case "uploading":
        return <div className="spinner-border spinner-border-sm ms-2" />;
      default:
        return null;
    }
  };

  const renderCard = (title, type) => {
    const hasExisting = !!fotosExistentes[type];
    const hasNew = !!images[type];

    return (
      <Card className="h-100 text-center p-3">
        <h5>{title} {renderStatusIcon(type)}</h5>
        {hasExisting || hasNew ? (
          <>
            <img
              src={hasNew ? images[type] : fotosExistentes[type]}
              alt={title}
              className="img-fluid rounded mb-2"
              style={{ maxHeight: "150px", objectFit: "cover" }}
            />
            {!hasExisting && (
              <div className="d-flex justify-content-center gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeImage(type)}
                  disabled={loading}
                >
                  Eliminar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openCamera(type)}
                  disabled={loading}
                >
                  <FaCamera className="me-1" /> Retomar
                </Button>
              </div>
            )}
          </>
        ) : (
          <Button
            variant="primary"
            onClick={() => openCamera(type)}
            disabled={loading}
          >
            <FaCamera className="me-1" /> Tomar Foto
          </Button>
        )}
      </Card>
    );
  };


  const totalImages = Object.values(images).filter(img => img).length;
  const successfulUploads = Object.values(uploadStatus).filter(status => status === "success").length;
  const progress = totalImages > 0 ? (successfulUploads / totalImages) * 100 : 0;

  return (
    <div className="container py-4">
      <h3 className="mb-4 text-center">Subir Documentación</h3>

      <div className="text-center mb-3">
        <small className="text-muted">
          Colaborador: Legajo {legajo} - CUIL {cuil}
        </small>
      </div>

      {message && (
        <Alert variant={
          message?.includes("❌") ? "danger" :
          message?.includes("⚠️") ? "warning" :
          "info"
        }>
          {message}
        </Alert>
      )}

      <Row className="g-4 mb-4">
        <Col md={4}>{renderCard("Selfie", "selfie")}</Col>
        <Col md={4}>{renderCard("DNI Frente", "dniFront")}</Col>
        <Col md={4}>{renderCard("DNI Dorso", "dniBack")}</Col>
        <Col md={4}>{renderCard("Carnet Frente", "carnetFront")}</Col>
        <Col md={4}>{renderCard("Carnet Dorso", "carnetBack")}</Col>
      </Row>

      {progress > 0 && (
        <div className="mb-3">
          <ProgressBar
            now={progress}
            label={`${Math.round(progress)}%`}
            variant={progress === 100 ? "success" : "primary"}
          />
          <small className="text-muted">
            {successfulUploads} de {totalImages} imágenes subidas
          </small>
        </div>
      )}

      <div className="text-center">
        <Button
          variant="success"
          size="lg"
          onClick={uploadAllImages}
          disabled={loading || totalImages === 0}
        >
          <FaUpload className="me-2" />
          {loading ? "Subiendo..." : "Subir Todas las Imágenes"}
        </Button>
      </div>

      <CameraCapture
        show={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCapture}
        title={
          cameraType === "selfie"
            ? "Tomar Selfie"
            : cameraType === "dniFront"
            ? "Capturar Frente del DNI"
            : cameraType === "dniBack"
            ? "Capturar Dorso del DNI"
            : cameraType === "carnetFront"
            ? "Capturar Frente del Carnet"
            : "Capturar Dorso del Carnet"
        }
        documentType={cameraType === "selfie" ? "selfie" : "document"}
      />
    </div>
  );
};

export default DocumentUpload;
